import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from '@mui/material';
import { Durchfuehrungszeitraum, MitarbeiterList } from '../../types';
import { ZeitraumFormState } from './auditDetailTypes';

interface ZeitraumDialogProps {
  open: boolean;
  isDark: boolean;
  editingZeitraum: Durchfuehrungszeitraum | null;
  zeitraumForm: ZeitraumFormState;
  mitarbeiter: MitarbeiterList[];
  zeitraumDateError: string;
  onClose: () => void;
  onSave: () => void;
  onChange: (next: ZeitraumFormState) => void;
}

const ZeitraumDialog: React.FC<ZeitraumDialogProps> = ({
  open,
  isDark,
  editingZeitraum,
  zeitraumForm,
  mitarbeiter,
  zeitraumDateError,
  onClose,
  onSave,
  onChange,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>
      {editingZeitraum ? 'Zeitraum bearbeiten' : 'Neuer Durchführungszeitraum'}
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Startdatum"
            type="date"
            value={zeitraumForm.startdatum}
            onChange={(event) => onChange({ ...zeitraumForm, startdatum: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Enddatum"
            type="date"
            value={zeitraumForm.enddatum}
            onChange={(event) => onChange({ ...zeitraumForm, enddatum: event.target.value })}
            InputLabelProps={{ shrink: true }}
            error={!!zeitraumDateError}
            helperText={zeitraumDateError}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Beschreibung"
            value={zeitraumForm.beschreibung}
            onChange={(event) => onChange({ ...zeitraumForm, beschreibung: event.target.value })}
            multiline
            rows={2}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Team-Mitarbeiter</InputLabel>
            <Select
              multiple
              value={zeitraumForm.mitarbeiter_ids}
              onChange={(event) =>
                onChange({
                  ...zeitraumForm,
                  mitarbeiter_ids: event.target.value as number[],
                })
              }
              input={<OutlinedInput label="Team-Mitarbeiter" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((value) => {
                    const person = mitarbeiter.find((entry) => entry.id === value);
                    return (
                      <Chip
                        key={value}
                        label={person ? `${person.vorname} ${person.nachname}` : value}
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {mitarbeiter.map((person) => (
                <MenuItem key={person.id} value={person.id}>
                  <Checkbox checked={zeitraumForm.mitarbeiter_ids.indexOf(person.id) > -1} />
                  <ListItemText primary={`${person.vorname} ${person.nachname}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Button
        onClick={onClose}
        sx={{ color: 'text.secondary', '&:hover': { bgcolor: isDark ? '#2c3358' : '#eef1f5' } }}
      >
        Abbrechen
      </Button>
      <Button
        variant="contained"
        onClick={onSave}
        disabled={!zeitraumForm.startdatum || !zeitraumForm.enddatum || !!zeitraumDateError}
      >
        {editingZeitraum ? 'Aktualisieren' : 'Erstellen'}
      </Button>
    </DialogActions>
  </Dialog>
);

export default ZeitraumDialog;
